import { Table, Button, Form, Input, Row, Col, Select, notification } from 'antd';
import { UserInfoMix } from './type';
import { atom } from 'nanostores';
import { useStore } from '@nanostores/react';
import { ColumnsType } from 'antd/es/table';
import { Credit, User } from '../api/go';
import { DateTime } from 'luxon';

const columns: ColumnsType<UserInfoMix> = [
  {
    title: '用户ID',
    dataIndex: 'UserId',
    key: 'UserId',
  },
  {
    title: '用户昵称',
    dataIndex: 'NickName',
    key: 'NickName',
  },
  {
    title: '账号状态',
    dataIndex: 'Status',
    key: 'Status',
    render(value) {
      if (value === 1 || value === '1') {
        return '正常';
      }
      return '封禁';
    },
  },
  {
    title: '封号备注',
    dataIndex: 'DisableRemark',
    key: 'DisableRemark',
  },
  {
    title: '对局数量 TT/OMG/MD',
    dataIndex: 'game_cnt',
    key: 'game_cnt',
    render(_, row) {
      const { game_stat } = row;
      return `${game_stat.dota_cnt} / ${game_stat.omg_cnt} / ${game_stat.md_cnt}`;
    },
  },
  {
    title: '举报数量',
    dataIndex: 'report_cnt',
    key: 'report_cnt',
    render(_, row) {
      const { report_count } = row;
      return `开图 ${report_count.cheating || 0}`;
    },
  },
  {
    title: '注册时间',
    dataIndex: 'UserRegtime',
    key: 'UserRegtime',
    render(value) {
      // 2025-12-03T16:38:00+08:00
      const reg = DateTime.fromISO(value);
      const date = reg.toFormat('yyyy-MM-dd');

      // 到今天为止的天数
      return `${date} (${reg.diffNow('days').negate().days.toFixed(0)} 天)`;
    },
  },
];

interface QueryForm {
  userIdList: string;
}

interface ActionForm {
  mark: string;
  ban_type: string;
  day_offset: number;
}

const DefaultData = {
  loading: false,
  list: [] as Array<UserInfoMix>,
};

const store = atom(DefaultData);

async function loadList(params: QueryForm) {
  // 批量加载用户信息
  store.set({ ...store.get(), loading: true });

  const userIdList = params.userIdList
    .split('\n')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  if (userIdList.length === 0) {
    store.set({ ...store.get(), loading: false, list: [] });
    return;
  }

  const resp = await User.infoMixList(userIdList);

  // 重新排序 按照 report_count.cheating
  const sorted = [...resp].sort((a, b) => {
    const aCheat = a.report_count.cheating || 0;
    const bCheat = b.report_count.cheating || 0;
    return bCheat - aCheat;
  });

  store.set({ ...store.get(), loading: false, list: sorted });
}

async function doBanAction(params: ActionForm) {
  const list = store.get().list;
  if (list.length === 0) {
    return;
  }

  let count = 0;
  const key = 'ban_list';

  notification.open({
    key,
    message: '封号进度',
    description: '禁用账号中',
  });

  // 批量将用户账号设置为封禁
  const res = await User.banList(
    list.map((u) => u.UserId),
    params.mark,
    params.day_offset || 0,
    {
      ban_type: params.ban_type || '未填写封禁类型',
    },
  );

  notification.open({
    key,
    message: '封号进度',
    description: `账号批量禁用成功 影响账号 ${res} 个`,
  });
  await new Promise((resolve) => setTimeout(resolve, 1000));

  for (const user of list) {
    // 清空信用分
    await Credit.update({
      user_id: user.UserId,
      score: 0,
      reason: params.mark,
      remark: params.mark,
    });

    count++;

    notification.open({
      key,
      message: '封号进度',
      description: `修改信用分 ${count}/${list.length} 完成`,
    });

    // 避免请求接口太快
    await new Promise((resolve) => setTimeout(resolve, 200));
  }

  notification.open({
    key,
    message: '封号进度',
    description: `用户封号处理完成`,
    duration: 1000,
  });

  // 重新加载列表
  loadList({ userIdList: list.map((u) => u.UserId).join('\n') });
}

const Reason: Record<string, string> = {
  PA1: `[PA1]检测到使用第三方插件，账号已封禁，有问题请联系客服 QQ 1094222127`,
  PA2: `[PA2]检测到使用第三方插件，账号已封禁，有问题请联系客服 QQ 1094222127`,
  PA3: `[PA3]检测到使用第三方插件，账号已封禁，有问题请联系客服 QQ 1094222127`,
  PA4: `[PA4]检测到使用第三方插件，账号已封禁，有问题请联系客服 QQ 1094222127`,
  PB: `[PB]在之前的对局中检测到开挂，账号已被封禁`,
  PB9: `[PB9]在之前的对局中检测到开挂，账号已被封禁`,
  PB7: `[PB7]在之前的对局中检测到开挂，账号已被封禁`,
  PB5: `[PB5]在之前的对局中检测到开挂，账号已被封禁`,
  PD: `[PD]在之前的对局中检测到开挂，账号已被封禁`,
  PD1: `[PD1]在之前的对局中检测到开挂，账号已被封禁`,
  PD2: `[PD2]在之前的对局中检测到开挂，账号已被封禁`,
  PDN: `[PDN]在之前的对局中检测到开挂，账号已被封禁`,
};

export default function CheatBan() {
  const state = useStore(store);

  return (
    <div>
      <div>
        <Row>
          <Col span={8}>
            <Form
              name="list"
              layout="horizontal"
              labelCol={{ span: 8 }}
              wrapperCol={{ span: 16 }}
              style={{ maxWidth: 600 }}
              onFinish={(values: QueryForm) => {
                if (values.userIdList) {
                  loadList(values);
                }
              }}
              autoComplete="off"
            >
              <Form.Item<QueryForm> label="User ID" name="userIdList" required>
                <Input.TextArea />
              </Form.Item>

              <Form.Item label={null}>
                <Button type="primary" htmlType="submit" loading={state.loading}>
                  查询
                </Button>
              </Form.Item>
            </Form>
          </Col>
          <Col span={8}>
            <Form
              name="action"
              layout="horizontal"
              labelCol={{ span: 8 }}
              wrapperCol={{ span: 16 }}
              style={{ maxWidth: 600 }}
              onFinish={(values: ActionForm) => {
                doBanAction(values);
              }}
              autoComplete="off"
            >
              <Form.Item<ActionForm> label="封号理由" name="mark" required>
                <Select
                  options={Object.keys(Reason).map((key) => ({
                    label: Reason[key],
                    value: Reason[key],
                  }))}
                />
              </Form.Item>

              <Form.Item<ActionForm> label="封号类型" name="ban_type" required>
                <Input.TextArea />
              </Form.Item>

              <Form.Item<ActionForm> label="日期偏移" name="day_offset" required>
                <Select
                  options={[
                    {
                      label: '0 天（今天）',
                      value: 0,
                    },
                    {
                      label: '1 天（昨天）',
                      value: -1,
                    },
                    {
                      label: '2 天（前天）',
                      value: -2,
                    },
                    {
                      label: '3 天',
                      value: -3,
                    },
                    {
                      label: '4 天',
                      value: -4,
                    },
                    {
                      label: '5 天',
                      value: -5,
                    },
                  ]}
                />
              </Form.Item>

              <Form.Item label={null}>
                <Button htmlType="submit" color="danger">
                  执行批量封号
                </Button>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </div>
      <div>
        <Table dataSource={state.list} columns={columns} pagination={false} />
      </div>
    </div>
  );
}
